import { inject, Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import {
  BulkDeleteOtdScansGQL,
  BulkDeleteOtdScansMutationVariables,
  CreateDownloadOtdScansFileGQL,
  CreateDownloadOtdScansFileMutationVariables,
  CreateOtdScanGQL,
  CreateOtdScanMutationVariables,
  DeleteOtdScanGQL,
  EditOtdScanGQL,
  EditOtdScanMutationVariables,
  GetOtdScanGQL,
  GetOtdScansGQL,
  GetOtdScansGroupedGQL,
  GetOtdScansGroupedQueryVariables,
  GetOtdScansQueryVariables,
  OtdScan,
} from '@uigy/generated/graphql';
import { Observable, Subject } from 'rxjs';
import { ApiPaths } from '@uigy/app.settings';
import { HttpRequestMethod } from '@uigy/shared/enums/http-request-method';
import { UploadFileService } from '@uigy/shared/services/upload-file/upload-file.service';

@Injectable({
  providedIn: 'root',
})
export class OtdScansService {
  private readonly getOtdScansGQL = inject(GetOtdScansGQL);
  private readonly getOtdScanGQL = inject(GetOtdScanGQL);
  private readonly getOtdScansGroupedGQL = inject(GetOtdScansGroupedGQL);
  private readonly createOtdScanGQL = inject(CreateOtdScanGQL);
  private readonly editOtdScanGQL = inject(EditOtdScanGQL);
  private readonly deleteOtdScanGQL = inject(DeleteOtdScanGQL);
  private readonly bulkDeleteOtdScansGQL = inject(BulkDeleteOtdScansGQL);
  private readonly createDownloadOtdScansFileGQL = inject(CreateDownloadOtdScansFileGQL);
  private readonly uploadFileService = inject(UploadFileService);


  public refetchData$ = new Subject<void>();

  public getOtdScansRaw(vars?: GetOtdScansQueryVariables) {
    return this.getOtdScansGQL.fetch(vars).pipe(take(1));
  }

  public getOtdScansGrouped(vars: GetOtdScansGroupedQueryVariables) {
    return this.getOtdScansGroupedGQL.fetch(vars).pipe(take(1));
  }

  public getOtdScan(id: string): Observable<OtdScan> {
    return this.getOtdScanGQL.fetch({ id }).pipe(
      map(response => response?.data?.otdScan as OtdScan),
      take(1),
    );
  }

  public createOtdScan(vars: CreateOtdScanMutationVariables): Observable<OtdScan> {
    return this.createOtdScanGQL.mutate(vars).pipe(
      take(1),
      map(response => response.data?.createOtdScan as OtdScan),
    );
  }

  public editOtdScan(vars: EditOtdScanMutationVariables): Observable<OtdScan> {
    return this.editOtdScanGQL.mutate(vars).pipe(
      take(1),
      map(response => response.data?.editOtdScan as OtdScan),
    );
  }

  public deleteOtdScan(id: string): Observable<boolean> {
    return this.deleteOtdScanGQL.mutate({ id }).pipe(
      map(response => response.data?.deleteOtdScan ?? false),
      take(1),
    );
  }

  public bulkDeleteOtdScans(vars: BulkDeleteOtdScansMutationVariables): Observable<boolean> {
    return this.bulkDeleteOtdScansGQL.mutate(vars).pipe(
      take(1),
      map(response => response.data?.bulkDeleteOtdScans ?? false),
    );
  }

  public createDownloadOtdScansFile(vars: CreateDownloadOtdScansFileMutationVariables): Observable<string | undefined> {
    return this.createDownloadOtdScansFileGQL.mutate(vars).pipe(
      take(1),
      map(response => response.data?.downloadOtdScans),
    );
  }

  public uploadOtdScanFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadFileService.uploadFile<any>(ApiPaths.uploadOtdScansResults, HttpRequestMethod.POST, {
      body: formData,
    });
  }
}
